const debounce = (fn, wait) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
};

class HeaderSearch extends HTMLElement {
  constructor() {
    super();

    this.searchButton = this.querySelector("[data-search-button]");
    this.searchForm = this.querySelector(".header__search");
    this.header = this.closest(".header");
    this.promoBar = document.querySelector(".promo-banner");

    // predictive search additions:
    this.input = this.querySelector("input[name='q']");
    this.results = this.querySelector("[data-results]");
    this.url = this.getAttribute("url");

    this.searchButton.addEventListener("click", () => {
      if (this.searchForm.classList.contains("active")) {
        this.closeSearchForm();
      } else {
        this.openSearchForm();
      }
    });

    this.addEventListener("click", (e) => {
      const closeButton = e.target.closest("[data-search-form-close]");

      if (closeButton) {
        this.closeSearchForm();
      }
    });

    window.addEventListener("click", (e) => {
      if (!this.contains(e.target)) {
        this.closeSearchForm();
      }
    });

    this._open = () => this.openSearchForm();

    this.closeEsc = (e) => {
      if (e.key === "Escape" && this.searchForm.classList.contains("active")) {
        this.closeSearchForm();
      }
    };

    // predictive search additions:
    const handleSearch = (input) => {
      if (input.value.trim() === "") {
        this.results.innerHTML = "";
        document.body.classList.remove("header-search-has-results");
        return;
      }
      this.getSearchResults(input.value.trim());
    };

    this.onTyping = debounce(handleSearch, 300);
  }

  connectedCallback() {
    document.addEventListener("keydown", this.closeEsc);
    document.addEventListener("header-search:open", this._open);
    // predictive search input handling
    this.input.addEventListener("input", (e) => {
      this.onTyping(this.input);
    });
  }

  disconnectedCallback() {
    document.removeEventListener("keydown", this.closeEsc);
    document.removeEventListener("header-search:open", this._open);
    this.input.removeEventListener("input", this.onTyping);
  }

  openSearchForm() {
    if (this.searchForm.classList.contains("active")) return;

    const barHeight = this.promoBar?.getBoundingClientRect().height || 0;
    const headerHeight = this.header.getBoundingClientRect().height;

    this.searchForm.style.setProperty(
      "--search-form-max-height",
      `calc(100vh - ${barHeight}px - ${headerHeight}px)`,
    );

    this.searchForm.style.setProperty(
      "--search-trigger-left",
      `${this.searchButton.offsetLeft}px`,
    );

    this.searchForm.style.display = "block";
    window.openDimmer(this);
    this.header && this.header.classList.add("search-is-opened");

    window.requestAnimationFrame(() => {
      this.searchForm.classList.add("active");
      window.trapFocus(this.searchForm, this.input);
      this.input.focus();
    });
  }

  closeSearchForm() {
    if (!this.searchForm.classList.contains("active")) return;
    this.searchForm.style.display = "none";
    const onTransitionEnd = () => {
      this.searchForm.removeEventListener("transitionend", onTransitionEnd);
      this.querySelector("live-search-wrapper")?.dispatchEvent(
        new CustomEvent("live-search-wrapper:clear"),
      );
      window.removeTrapFocus(this.searchButton);
    };

    this.searchForm.addEventListener("transitionend", onTransitionEnd);

    this.searchForm.classList.remove("active");
    window.closeDimmer(this);
    this.header && this.header.classList.remove("search-is-opened");

    // clear predictive search state
    this.input.value = "";
    this.results.innerHTML = "";
    document.body.classList.remove("header-search-has-results");
  }

  getSearchResults(q) {
    let requestResponse;

    window.theme
      .fetch(
        `${this.url}?q=${q}&section_id=predictive-search&resources[limit]=10&resources[limit_scope]=each`,
      )
      .then((response) => {
        requestResponse = response;
        return response.text();
      })
      .then((text) => {
        if (!requestResponse.ok) {
          throw new Error(`${requestResponse.status}: ${text}`);
        }

        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;

        this.results.innerHTML = resultsMarkup;
      })
      .catch((error) => {
        console.error(error);
      });
  }
}

if (!customElements.get("header-search")) {
  customElements.define("header-search", HeaderSearch);
}
